import { useEffect, useState } from "react";
import { useAuth } from "./auth/useAuth";
import { completeSignIn } from "./auth/oidc";
import { Browse } from "./routes/Browse";
import { Playlists } from "./routes/Playlists";
import { Decks } from "./routes/Decks";
import { Admin } from "./routes/admin/Admin";
import { forgetFavorites, loadFavorites } from "./favorites";
import { setRights, useRights } from "./rights";
import { api } from "./api/client";

export function App() {
  const auth = useAuth();
  const [path, setPath] = useState(window.location.pathname);
  // The query string too, so that going back to a filtered page restores the
  // filters: Browse reads them once when it mounts, and is remounted when the
  // history moves. Its own filter changes replace the URL without an event.
  const [search, setSearch] = useState(window.location.search);
  const [shopCount, setShopCount] = useState<number | null>(null);

  useEffect(() => {
    const pop = () => { setPath(window.location.pathname); setSearch(window.location.search); };
    window.addEventListener("popstate", pop);
    return () => window.removeEventListener("popstate", pop);
  }, []);

  // Once signed in: tell the API we are here (that is how it learns a subject
  // exists), learn what we may do, and load the favorites every card will
  // ask about -- only where we have that feature; the API would refuse.
  useEffect(() => {
    if (!auth.user) { forgetFavorites(); setRights([]); return; }
    api.shops().then((s) => setShopCount(s.length)).catch(() => { /* the blurb says "the" */ });
    api.me()
      .then((me) => {
        setRights(me.rights ?? []);
        if (me.rights?.includes("favorites")) {
          void loadFavorites().catch(() => { /* hearts stay empty; the page is usable */ });
        }
      })
      .catch(() => { /* nothing granted until the API answers */ });
  }, [auth.user]);
  const rights = useRights();

  // Coming back from Keycloak. The code is exchanged here and the reader is
  // put back where they were, not on the front page.
  if (path === "/auth/callback") return <Callback onDone={setPath} />;

  if (auth.loading) return <div className="loading"><span className="spinner" />…</div>;
  if (!auth.user) return <SignIn onSignIn={auth.signIn} />;

  // Five route families, read off the path by hand -- see go() below. The
  // admin panel's pages are in the path too, so the back gesture moves
  // between them rather than out of the panel.
  const pl = /^\/playlists\/(\d+)$/.exec(path);
  const shared = /^\/p\/([A-Za-z0-9_-]+)$/.exec(path);
  const admin = /^\/admin(?:\/([a-z]+))?\/?$/.exec(path);
  const section = admin ? "admin"
    : path === "/playlists" || pl || shared ? "playlists"
    : path === "/decks" ? "decks"
    : "browse";
  const playlistID = pl ? Number(pl[1]) : undefined;
  const shareToken = shared?.[1];

  return (
    <>
      <header>
        <div className="wrap" style={{ paddingBottom: 0 }}>
          <h1>Record shop listings, on Bandcamp</h1>
          <div className="sub">
            New arrivals and pre-orders from {shopCount ?? "the"} record shops, matched to
            Bandcamp releases and tagged with Bandcamp&rsquo;s own tags.
          </div>
          <div className="topbar">
            <button className={`nav${section === "browse" ? " on" : ""}`}
                    onClick={() => go("/", setPath)}>Browse</button>
            {rights.has("playlists") && (
              <button className={`nav${section === "playlists" ? " on" : ""}`}
                      onClick={() => go("/playlists", setPath)}>Playlists</button>
            )}
            {rights.has("decks") && (
              <button className={`nav${section === "decks" ? " on" : ""}`}
                      onClick={() => go("/decks", setPath)}>Beat match</button>
            )}
            {/* Shown only to those who would be let in -- the token and
                /api/me agree on that, and the API enforces it regardless. */}
            {auth.isAdmin && (
              <button className={`nav${section === "admin" ? " on" : ""}`}
                      onClick={() => go("/admin", setPath)}>Admin</button>
            )}
            <span className="who">
              <b>{auth.username}</b>
              <button className="link" onClick={auth.signOut}>sign out</button>
            </span>
          </div>
        </div>
      </header>
      <div className="wrap">
        {section === "admin" && <Admin page={admin?.[1]} go={(p) => go(p, setPath)} />}
        {section === "playlists" && (rights.has("playlists") ? (
          <Playlists id={playlistID} token={shareToken} go={(p) => go(p, setPath)} />
        ) : (
          <div className="empty">Playlists are not switched on for your account. An admin can give you the feature.</div>
        ))}
        {section === "decks" && (rights.has("decks") ? (
          <Decks />
        ) : (
          <div className="empty">Beat match is not switched on for your account. An admin can give you the feature.</div>
        ))}
        {section === "browse" && <Browse key={search} />}
      </div>
      <footer>
        Covers and players come from Bandcamp; prices are each shop&rsquo;s own,
        in its own currency.
        <span className="version" title="the build this page came from">
          {import.meta.env.VITE_APP_VERSION || "dev"}
        </span>
      </footer>
    </>
  );
}

function SignIn({ onSignIn }: { onSignIn: () => void }) {
  return (
    <div className="signin">
      <h1>Record shop listings, on Bandcamp</h1>
      <p>
        Record shops&rsquo; new arrivals and pre-orders, matched to Bandcamp
        and searchable by tag, shop, label, date and tempo.
      </p>
      <p>This catalogue is private. Sign in to browse it.</p>
      <button onClick={onSignIn}>Sign in</button>
    </div>
  );
}

function Callback({ onDone }: { onDone: (path: string) => void }) {
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    completeSignIn()
      .then((returnTo) => {
        window.history.replaceState(null, "", returnTo);
        onDone(new URL(returnTo, window.location.origin).pathname);
      })
      .catch((e: Error) => setError(e.message));
  }, [onDone]);

  if (error) {
    return (
      <div className="signin">
        <h1>Sign-in did not complete</h1>
        <p>{error}</p>
        <button onClick={() => { window.location.href = "/"; }}>Try again</button>
      </div>
    );
  }
  return <div className="loading"><span className="spinner" />signing in…</div>;
}

// Client-side navigation, hand-rolled: a handful of routes and a redirect
// callback do not justify a router.
function go(path: string, setPath: (p: string) => void) {
  window.history.pushState(null, "", path);
  setPath(path);
}
